import React, { useCallback, useMemo, useState, useContext } from 'react'

import { Modal, Upload, Progress, Menu, Dropdown, Alert } from 'antd'
import { connect } from 'react-redux'
import { getActiveLanguage, getTranslate } from 'react-localize-redux'
import { faDownload, faFileImport } from '@fortawesome/pro-solid-svg-icons'
import { getUser } from '../../../reducers/UserReducer'
import { getLanguageByLocale } from '../../../utils'
import { callFunctionIfDefined } from '../../../utils/functionHelper'
import { onError, onSuccess } from '../../../utils/apiHelper'
import { getInternshipsImportTemplate, importInternships } from '../../../utils/api/internship'
import TooltipIconButton from '../../antd/Buttons/TooltipIconButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useFakeProgress from '../../../hooks/useFakeProgress'

import '../../../assets/students-import-modal.scss'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { ColorsPalette } from '../../../config/colors'
import { SPREADSHEET_UPLOAD_TYPES } from '../../../utils/constants'

const { Dragger } = Upload

const mapStateToProps = state => ({
  t: getTranslate(state.locale),
  activeLanguage: getActiveLanguage(state.locale).code,
  user: getUser(state.getUser)
})

const ImportInternshipsButton = ({ activeLanguage, school, user, t, afterUpload }) => {
  const { isDraft } = useContext(GlobalContext)

  const [visible, setVisible] = useState(false)
  const [importing, setImporting] = useState(false)
  const [fileList, setFilesList] = useState([])

  const { percentage: importPercentage, start: startProgress, reset: resetProgress } = useFakeProgress()

  const acceptedTypes = useMemo(() => SPREADSHEET_UPLOAD_TYPES.join(','), [])

  const resetStates = useCallback(() => {
    setFilesList([])
    setImporting(false)
    resetProgress()
  }, [resetProgress])

  const handleFileUpload = async () => {
    if (fileList.length > 0) {
      setImporting(true)

      if (SPREADSHEET_UPLOAD_TYPES.includes(fileList[0].type)) {
        startProgress()

        importInternships(
          user,
          fileList[0],
          { school, language: getLanguageByLocale(activeLanguage), isDraft: isDraft },
          () => {
            callFunctionIfDefined(afterUpload)
            handleClose()
            onSuccess(t('import_internships_button.import.success'))
          },
          () => {
            callFunctionIfDefined(afterUpload)
            handleClose()
            onError(t('import_internships_button.import.error'))
          }
        )
      }
    }
  }

  const downloadTemplate = () => {
    getInternshipsImportTemplate(user, { language: getLanguageByLocale(activeLanguage), school: school }).then(() => {
      onSuccess(t('import_internships_button.download_template.success'))
    }).catch(() => {
      onError(t('import_internships_button.download_template.error'))
    })
  }

  const handleClose = useCallback(() => {
    resetStates()
    setVisible(false)
  }, [resetStates])

  const menu = (
    <Menu>
      <Menu.Item onClick={() => setVisible(true)}>
        <FontAwesomeIcon icon={faFileImport} />
        {t('import_internships_button.tooltip_title')}
      </Menu.Item>
      <Menu.Item onClick={downloadTemplate}>
        <FontAwesomeIcon icon={faDownload} />
        {t('import_internships_button.download_template')}
      </Menu.Item>
    </Menu>
  )

  return (
    <>
      <Dropdown overlay={menu} trigger={['click']}>
        <TooltipIconButton
          title={t('import_internships_button.tooltip_title')}
          icon={faFileImport}
        />
      </Dropdown>
      <Modal
        title={t('import_internships_button.modal_title')}
        visible={visible}
        onCancel={handleClose}
        onOk={handleFileUpload}
        okText={t('Import')}
        cancelText={t('Cancel')}
        okButtonProps={{ disabled: fileList.length === 0 }}
        destroyOnClose
      >
        <div className='students-import-modal-body ai-ctr'>
          {isDraft && (
            <Alert
              type='warning'
              message={
                <span style={{ color: ColorsPalette.GREY }}>{t('import_internships_button.warn.is_draft')}</span>
              }
              showIcon
              style={{
                marginBottom: '10px'
              }}
            />
          )}
          {!importing && (
            <Dragger
              fileList={fileList}
              name='file'
              accept={acceptedTypes}
              beforeUpload={(file) => {
                setFilesList([file])

                return false
              }}
              onRemove={() => setFilesList([])}
            >
              <p className='ant-upload-text'>
                {t('import_internships_button.upload_text')}{' '}
              </p>
              <p className='ant-upload-hint'>
                {t('import_internships_button.upload_hint')}{' '}
              </p>
            </Dragger>
          )}
          {importing && (
            <div className='importing-animation'>
              <Progress type='circle' percent={importPercentage} />
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

export default connect(mapStateToProps)(ImportInternshipsButton)
